import React, { useEffect, useMemo, useState } from "react";
import ModalBackdrop from "components/ModalBackdrop";
import {
  DropdownSelect,
  TextAreaInput,
  TextInput,
} from "components/InputStyled";
import {
  flattenClassificationHierarchy,
  getClassificationPath,
} from "../lib/hierarchy";

function eligibleOptions(classification, projectId, projectAssignments) {
  const eligibleIds = new Set(
    projectAssignments
      .filter(
        (assignment) =>
          assignment.project_id === projectId &&
          assignment.classification_id === classification.id
      )
      .map((assignment) => assignment.classification_value_id)
  );
  return flattenClassificationHierarchy(classification.values)
    .filter((value) => value.is_active && eligibleIds.has(value.id))
    .map((value) => ({
      value: value.id,
      label: getClassificationPath(classification.values, value.id),
    }));
}

export default function AllocationComponentModal({
  component,
  initialAssignments = [],
  projects,
  classifications,
  projectAssignments,
  saving,
  onSave,
  onDelete,
  onClose,
}) {
  const [draft, setDraft] = useState({ project_id: "", component_code: "", description: "", notes: "", is_active: true });
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    setDraft({ project_id: "", component_code: "", description: "", notes: "", is_active: true, ...(component || {}) });
    const grouped = {};
    initialAssignments.forEach((assignment) => {
      if (!grouped[assignment.classification_id]) {
        grouped[assignment.classification_id] = [assignment.classification_value_id];
      }
    });
    if (component?.project_id) {
      classifications.forEach((classification) => {
        if ((grouped[classification.id] || []).length) return;
        const options = eligibleOptions(
          classification,
          component.project_id,
          projectAssignments
        );
        if (options.length === 1) {
          grouped[classification.id] = [options[0].value];
        }
      });
    }
    setAssignments(grouped);
  }, [classifications, component, initialAssignments, projectAssignments]);

  const missingRequired = useMemo(() => classifications.some((classification) => {
    const scope = classification.scopes.find((item) => item.object_type === "debt_allocation_component");
    const eligibleIds = new Set(
      eligibleOptions(classification, draft.project_id, projectAssignments).map(
        (option) => option.value
      )
    );
    const selectedEligible = (assignments[classification.id] || []).filter(
      (valueId) => eligibleIds.has(valueId)
    );
    return scope?.is_required && !selectedEligible.length;
  }), [assignments, classifications, draft.project_id, projectAssignments]);

  const assignmentPayload = classifications.map((classification) => {
    const eligibleIds = new Set(
      eligibleOptions(classification, draft.project_id, projectAssignments).map(
        (option) => option.value
      )
    );
    return {
      classification_id: classification.id,
      classification_value_ids: (assignments[classification.id] || []).filter(
        (valueId) => eligibleIds.has(valueId)
      ).slice(0, 1),
    };
  });

  const handleProjectChange = (project_id) => {
    const nextAssignments = {};
    classifications.forEach((classification) => {
      const options = eligibleOptions(
        classification,
        project_id,
        projectAssignments
      );
      nextAssignments[classification.id] =
        options.length === 1 ? [options[0].value] : [];
    });
    setDraft((current) => ({ ...current, project_id }));
    setAssignments(nextAssignments);
  };

  return (
    <ModalBackdrop onClose={saving ? undefined : onClose}>
      <form className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-app p-6 shadow-xl" onSubmit={(event) => { event.preventDefault(); onSave(draft, assignmentPayload); }}>
        <h2 className="text-lg font-semibold text-brand">{component?.id ? "Edit Allocation Component" : "New Allocation Component"}</h2>
        <p className="mt-1 text-sm text-muted">A component identifies one Project and the classifications applying to its portion of this debt issue.</p>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <DropdownSelect label="Project" value={draft.project_id} onChange={handleProjectChange} options={projects.map((project) => ({ value: project.id, label: project.project_code ? `${project.project_code} — ${project.project_name}` : project.project_name }))} placeholder="Select a Project" className="w-full" required />
          <TextInput label="Component Code" value={draft.component_code} onChange={(component_code) => setDraft((current) => ({ ...current, component_code }))} className="w-full" />
        </div>

        {classifications.length ? <div className="mt-5 rounded-lg border border-border p-4">
          <div className="font-medium text-app">Component classifications</div>
          <div className="mt-3 grid gap-4 md:grid-cols-2">{classifications.map((classification) => {
            const scope = classification.scopes.find((item) => item.object_type === "debt_allocation_component");
            const selected = assignments[classification.id] || [];
            const options = eligibleOptions(
              classification,
              draft.project_id,
              projectAssignments
            );
            if (!options.length) return <div key={classification.id}><div className="text-xs font-medium text-muted">{classification.name}{scope.is_required ? " *" : ""}</div><div className={`mt-1 rounded-md border px-3 py-2 text-sm ${scope.is_required ? "border-red-500/60 text-red-600" : "border-border text-muted"}`}>{draft.project_id ? "No values are enabled for this Project." : "Select a Project first."}</div></div>;
            if (options.length === 1) return <div key={classification.id}><div className="text-xs font-medium text-muted">{classification.name}{scope.is_required ? " *" : ""}</div><div className="mt-1 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm text-app">{options[0].label}</div></div>;
            return <DropdownSelect key={classification.id} label={classification.name} value={selected[0] || ""} onChange={(value) => setAssignments((current) => ({ ...current, [classification.id]: value ? [value] : [] }))} options={options} placeholder={scope.is_required ? "Select a required Project value" : "No value"} className="w-full" required={scope.is_required} />;
          })}</div>
        </div> : null}

        <div className="mt-4 space-y-4">
          <TextAreaInput label="Description" value={draft.description} onChange={(description) => setDraft((current) => ({ ...current, description }))} rows={2} />
          <TextAreaInput label="Notes" value={draft.notes} onChange={(notes) => setDraft((current) => ({ ...current, notes }))} rows={2} />
        </div>
        <div className="mt-6 flex items-center justify-between gap-2">
          <div>{component?.id ? <button type="button" className="btn bg-red-600 text-white hover:bg-red-700" onClick={onDelete} disabled={saving}>Delete Component</button> : null}</div>
          <div className="flex gap-2"><button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button><button type="submit" className="btn btn-primary" disabled={saving || !draft.project_id || missingRequired}>{saving ? "Saving…" : "Save Component"}</button></div>
        </div>
      </form>
    </ModalBackdrop>
  );
}
